import { nucleusConstants, servicesSelectors } from '@lifesize/nucleus';
import { remotingActions, remotingConstants } from '@lifesize/clients.remoting';
import { Action, AnyAction, Dispatch, Middleware, MiddlewareAPI } from 'redux';
import { get } from 'lodash';
import Actions from 'actions/presentation';
import ActionsHome from 'actions/home';
import ActionTypes from 'constants/ActionTypes';
import ScreenSharing from 'constants/ScreenSharing';
import homeSelector from 'selectors/home';
import { login, logout } from 'utils/presenting';

const cancelFailedPresentationAlertDelay: number = 10000;
const stopUnansweredPresentationDelay: number = 5000;
const maxFrameRate = 30;
const maxWidth = 1920;
const maxHeight = 1080;
const displayMediaConstraints: MediaStreamConstraints = {
  video: {
    frameRate: { max: maxFrameRate },
    width: { max: maxWidth },
    height: { max: maxHeight }
  },
  audio: true
};
let installationCheckInterval: number;
let isListeningForExtension: boolean = false;
let showFailedPresentationAlertTimeout: number;
let stopUnansweredPresentationTimeout: number;

function getPresentationStream(sourceId: string) {
  const constraints = {
    audio: true,
    video: {
      mandatory: {
        chromeMediaSource: 'desktop',
        chromeMediaSourceId: sourceId,
        maxWidth,
        maxHeight,
        maxFrameRate
      }
    } as any
  };
  return navigator.mediaDevices.getUserMedia(constraints);
}

async function handleScreenShareEvent(store: MiddlewareAPI, e: any) {
  // if the streamData exists, a sharing source was selected (start the presentation), otherwise the selector was dismissed
  const streamData = get(e, 'data');
  if (streamData) {
    store.dispatch(Actions.startPresenting(await getPresentationStream(streamData)));
  }
}

async function initiateChromePresentation(store: MiddlewareAPI) {
  if (get(navigator, 'mediaDevices.getDisplayMedia')) {
    // @ts-ignore
    const stream = await navigator.mediaDevices.getDisplayMedia(displayMediaConstraints);
    if (stream) {
      store.dispatch(Actions.startPresenting(stream));
    }
    return;
  }

  const extensionElement = document.getElementById(ScreenSharing.extensionElementId);
  // Add listeners (once)
  if (!isListeningForExtension) {
    document.addEventListener(ScreenSharing.extensionEventName, (e) => handleScreenShareEvent(store, e));
    document.addEventListener(ScreenSharing.extensionReinstall, () => store.dispatch(Actions.installExtension()));
    isListeningForExtension = true;
  }

  if (extensionElement) {
    const canPresent = get(store.getState(), 'app.remoting.controller.canPresent');
    // only show the sharing selector if the presenter is eligible to present
    if (canPresent) {
      showChromeExtensionPresentationSelector(extensionElement);
    }
  }
  else {
    store.dispatch(Actions.installExtension());
  }
}

function goToStoreAndWaitForInstall(store: MiddlewareAPI) {
  let extensionElement = document.getElementById(ScreenSharing.extensionElementId);
  // clean up extensin element if it has been uninstalled
  if (extensionElement && extensionElement.parentNode) {
    extensionElement.parentNode.removeChild(extensionElement);
  }
  window.open(ScreenSharing.extensionUrl, '_blank');
  let installationCheckCount = 0;
  const installationCheck = () => {
    extensionElement = document.getElementById(ScreenSharing.extensionElementId);
    if (extensionElement) {
      const canPresent = get(store.getState(), 'app.remoting.controller.canPresent');
      store.dispatch(Actions.dismissExtensionInstall());
      // only show the sharing selector if the presenter is eligible to present
      if (canPresent) {
        showChromeExtensionPresentationSelector(extensionElement);
      }
    } else {
      installationCheckCount += 1;
    }
    if (installationCheckCount > 300) {
      store.dispatch(Actions.dismissExtensionInstall());
    }
  };
  installationCheckInterval = window.setInterval(installationCheck, 1000);
}

function showChromeExtensionPresentationSelector(extensionElement: any): any {
  const ev = new CustomEvent(ScreenSharing.extensionRequestName, {
    bubbles: false,
    cancelable: false,
    detail: ['screen', 'window', 'tab']
  });
  extensionElement.dispatchEvent(ev);
}

const presentationMiddleware = (store: MiddlewareAPI | any) => (next: Dispatch<AnyAction>) => (action: Action) => {
  const result = next(action);
  const state = store.getState();
  switch (action.type) {
    case ActionTypes.EndExtensionInstall:
      window.clearInterval(installationCheckInterval);
      break;
    case ActionTypes.WaitForExtensionInstall:
      goToStoreAndWaitForInstall(store);
      break;
    case ActionTypes.StartPresenting:
      const canPresent = get(store.getState(), 'app.remoting.controller.canPresent');
      if (!servicesSelectors.selectConnected) {
        store.dispatch(Actions.setPresentationError(true));
        window.clearTimeout(showFailedPresentationAlertTimeout);
        showFailedPresentationAlertTimeout = window.setTimeout(() => store.dispatch(Actions.setPresentationError(false)), cancelFailedPresentationAlertDelay);
      }
      // only create the offer if the presenter is eligible to present
      else if (canPresent)
        store.dispatch(Actions.createWirelessOffer(get(action, 'payload')));
      break;
    case ActionTypes.StopPresenting:
      store.dispatch(Actions.sendWirelessEnd());
      break;
    case ActionTypes.ShowPresentationSelector:
      initiateChromePresentation(store);
      break;
    case ActionTypes.SubmitPresenterCode:
      const linkingCode = get(action, 'payload.linkingCode');
      if (linkingCode) { // the button in the UI is disabled if there is no linkingCode
        store.dispatch(remotingActions.pairing.getAccountDetails(linkingCode, remotingConstants.pairing.linkedDeviceTypes.presenter));
      }
      break;
    case ActionTypes.StopUnansweredPresentation:
      const { iceConnectionState, sending } = get(state, 'app.remoting.wirelessPresentation', {});
      const iceConnected = get(nucleusConstants, 'rtcIceConnectionState.connected');
      const iceCompleted = get(nucleusConstants, 'rtcIceConnectionState.completed');
      if (sending && iceConnectionState !== iceConnected && iceConnectionState !== iceCompleted) {
        store.dispatch(ActionsHome.stopPresenting());
      }
      break;
    case remotingConstants.pairing.actions.GET_PRESENTER_TOKEN_SUCCESS:
      login(store, get(action, 'payload.jwt.A'));
      break;
    case nucleusConstants.INITIALIZE_SERVICES_SUCCESS:
      store.dispatch(ActionsHome.showPresentationSelector());
      const deviceMessagingSubscription = get(store.getState(), 'nucleus.proxy.subscriptions')['ls.deviceMessaging'];
      if (deviceMessagingSubscription) {
        store.dispatch(remotingActions.remoting.requestState(''));
      }
      break;
    case nucleusConstants.WAMP_CONNECT_ERROR:
      // attempt to recover presentation if presentation has started
      if (homeSelector(state).isPresenting) {
        // stop presentation
        store.dispatch(Actions.setPresentationError(true));
        store.dispatch(ActionsHome.stopPresenting());
        window.clearTimeout(showFailedPresentationAlertTimeout);
        showFailedPresentationAlertTimeout = window.setTimeout(() => store.dispatch(Actions.setPresentationError(false)), cancelFailedPresentationAlertDelay);
      }
      break;
    case nucleusConstants.WAMP_CONNECT_SUCCESS:
      store.dispatch(remotingActions.remoting.subscribeToEvents()); // subscribes to ls.deviceMessaging
      break;
    case remotingConstants.pairing.actions.DISPLAY_UNPAIRED:
      logout(store);
      break;
    // REMOTE_STATE_UPDATED is the action sent for all display state updates
    case remotingConstants.remoting.actions.REMOTE_STATE_UPDATED:
      const { activeWirelessPresenter, displayIsAvailable } = get(state, 'app.remoting.controller', {});
      const uuid = get(state, 'app.uuid');
      const { isPresenterAuthenticated, isPresenting } = homeSelector(state);
      if (isPresenting && ((activeWirelessPresenter !== uuid) || !displayIsAvailable)) {
        // the presentation has been stopped by the display, so stop it here too
        store.dispatch(Actions.sendWirelessEnd({ onlyEndSelf: true }));
      }
      if (isPresenterAuthenticated && !displayIsAvailable) {
        logout(store);
      }
      break;
    case remotingConstants.remoting.actions.WIRELESS_PRESENTATION_SEND_START:
      window.clearTimeout(stopUnansweredPresentationTimeout);
      stopUnansweredPresentationTimeout = window.setTimeout(() => store.dispatch(Actions.stopUnansweredPresentation()), stopUnansweredPresentationDelay);
      break;
    case remotingConstants.remoting.actions.WIRELESS_PRESENTATION_ANSWER_RECEIVED:
      window.clearTimeout(stopUnansweredPresentationTimeout);
      break;
  }
  return result;
};

export default presentationMiddleware as Middleware;



// WEBPACK FOOTER //
// ./src/middleware/presentation.ts